// frontend/src/pages/PoliticaValidacao.jsx
import { useState } from "react";
import { ArrowLeft, Download } from "lucide-react";

export default function PoliticaValidacao() {
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  const pdfUrl = `${process.env.PUBLIC_URL}/pdfs/politica-validacao.pdf`;

  const handleDownloadPDF = () => {
    setIsGeneratingPDF(true);
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = 'politica-validacao.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setIsGeneratingPDF(false);
  };

  return (
    <>
      <style>
        {`
          @media print {
            body { margin: 0; padding: 0; background: white; }
            .no-print { display: none !important; }
            .print-content { margin: 0; padding: 0.5cm; width: 100%; }
            .print-content h1 { font-size: 18pt; margin-bottom: 10pt; }
            .print-content h2 { font-size: 14pt; margin-top: 15pt; margin-bottom: 8pt; page-break-after: avoid; }
            .print-content p { font-size: 10pt; line-height: 1.4; margin-bottom: 6pt; page-break-inside: avoid; }
            .print-content ul { margin: 0 0 8pt 0; padding-left: 20pt; }
            .print-content li { font-size: 10pt; margin-bottom: 3pt; page-break-inside: avoid; }
            .print-content .section { page-break-inside: avoid; margin-bottom: 12pt; }
            .print-content .border-b { border-bottom: 0.5pt solid #ccc; }
            @page { size: A4; margin: 1.5cm; }
          }
        `}
      </style>

      <div className="min-h-screen bg-gray-50 pt-2 pb-12">
        <div className="max-w-4xl mx-auto px-4">
          {/* Header */}
          <div className="no-print bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <img
                  src={`${process.env.PUBLIC_URL}/img/logo2.png`}
                  alt="Brechó Vizinho"
                  className="h-12 w-auto object-contain"
                />
                <div>
                  <h1 className="text-xl font-bold text-gray-800">Política de Validação</h1>
                  <p className="text-xs text-gray-500">Versão 1.0 | 15/06/2026</p>
                </div>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => window.history.back()}
                  className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span className="hidden sm:inline">Voltar</span>
                </button>
                <button
                  onClick={handleDownloadPDF}
                  disabled={isGeneratingPDF}
                  className="flex items-center gap-2 px-4 py-2 bg-[#449879] text-white rounded-lg hover:bg-[#2d5f4d] transition-colors disabled:opacity-50"
                >
                  {isGeneratingPDF ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Download className="w-4 h-4" />
                  )}
                  <span className="hidden sm:inline">Baixar PDF</span>
                </button>
              </div>
            </div>
          </div>

          {/* Conteúdo */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 md:p-8 print:shadow-none print:p-0 print:bg-white">
            <div className="print-content prose prose-sm max-w-none prose-headings:text-gray-800 prose-p:text-gray-600 prose-strong:text-gray-800">
              
              {/* TÍTULO */}
              <div className="text-center mb-8 print:mb-4">
                <h1 className="text-2xl font-bold text-gray-800 mb-2">POLÍTICA DE VALIDAÇÃO DE MORADORES E PROPRIEDADES</h1>
                <p className="text-sm text-gray-500">Versão 1.0 | Data de Vigência: 15/06/2026</p>
                <div className="w-20 h-1 bg-[#449879] mx-auto mt-3 rounded-full print:bg-gray-400" />
              </div>

              {/* INTRODUÇÃO */}
              <p className="text-gray-600 leading-relaxed mb-4">
                O Brechó Vizinho foi desenvolvido para promover interações seguras entre moradores de comunidades
                participantes.
              </p>
              <p className="text-gray-600 leading-relaxed mb-4">
                Para preservar a confiança da comunidade e reduzir riscos de fraudes, a plataforma poderá realizar
                procedimentos de validação de usuários, imóveis e vínculos residenciais.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Esta Política estabelece as regras aplicáveis ao processo de validação.
              </p>

              {/* 1. OBJETIVO DA VALIDAÇÃO */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">1. OBJETIVO DA VALIDAÇÃO</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  A validação tem como finalidade:
                </p>
                <ul className="list-disc list-inside space-y-1 mb-2 text-gray-600 ml-4">
                  <li>Confirmar o vínculo do usuário com determinada comunidade;</li>
                  <li>Aumentar a segurança da plataforma;</li>
                  <li>Reduzir cadastros fraudulentos;</li>
                  <li>Proteger moradores e proprietários;</li>
                  <li>Garantir a confiabilidade das interações entre usuários;</li>
                  <li>Preservar o caráter comunitário do Brechó Vizinho.</li>
                </ul>
                <p className="text-gray-600 leading-relaxed">
                  A validação não representa certificação de identidade civil, situação financeira, antecedentes ou qualquer outra
                  condição pessoal do usuário.
                </p>
              </div>

              {/* 2. TIPOS DE USUÁRIOS */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">2. TIPOS DE USUÁRIOS</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  A plataforma poderá disponibilizar diferentes categorias de usuários.
                </p>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Inicialmente:
                </p>
                <ul className="list-disc list-inside space-y-1 mb-2 text-gray-600 ml-4">
                  <li>Proprietário</li>
                  <li>Morador</li>
                </ul>
                <p className="text-gray-600 leading-relaxed">
                  Outras categorias poderão ser criadas futuramente sem necessidade de alteração desta Política.
                </p>
              </div>

              {/* 3. CADASTRO DE PROPRIETÁRIOS */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">3. CADASTRO DE PROPRIETÁRIOS</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  O usuário que solicitar cadastro como Proprietário deverá fornecer as informações exigidas pela plataforma.
                </p>
                <p className="text-gray-600 leading-relaxed mb-2">
                  O Brechó Vizinho poderá solicitar documentos ou informações adicionais para validar a relação do usuário
                  com o imóvel informado.
                </p>
                <p className="text-gray-600 leading-relaxed mb-2">
                  A aprovação não constitui reconhecimento de propriedade perante órgãos públicos ou terceiros.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  A validação destina-se exclusivamente ao funcionamento interno da plataforma.
                </p>
              </div>

              {/* 4. CADASTRO DE MORADORES */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">4. CADASTRO DE MORADORES</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Usuários cadastrados como Moradores poderão estar sujeitos à validação antes da ativação definitiva da conta.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  O objetivo é confirmar a vinculação do usuário ao imóvel ou à comunidade informada.
                </p>
              </div>

              {/* 5. PROCESSO DE VALIDAÇÃO */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">5. PROCESSO DE VALIDAÇÃO</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  A validação poderá ocorrer por diferentes meios, incluindo:
                </p>
                <ul className="list-disc list-inside space-y-1 mb-2 text-gray-600 ml-4">
                  <li>Confirmação pelo Proprietário validado;</li>
                  <li>Confirmação pela Administração do condomínio;</li>
                  <li>Confirmação pelo Síndico;</li>
                  <li>Confirmação por Associação de Moradores;</li>
                  <li>Confirmação por Conselho Gestor da comunidade;</li>
                  <li>Análise realizada pelo Administrador da plataforma;</li>
                  <li>Outros mecanismos de validação que venham a ser implementados.</li>
                </ul>
                <p className="text-gray-600 leading-relaxed">
                  O Brechó Vizinho poderá combinar diferentes métodos de validação sempre que considerar necessário.
                </p>
              </div>

              {/* 6. COMPARTILHAMENTO DE DADOS PARA VALIDAÇÃO */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">6. COMPARTILHAMENTO DE DADOS PARA VALIDAÇÃO</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Para viabilizar a validação, o usuário autoriza o compartilhamento limitado das informações estritamente
                  necessárias com as entidades responsáveis pela análise.
                </p>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Essas informações poderão incluir:
                </p>
                <ul className="list-disc list-inside space-y-1 mb-2 text-gray-600 ml-4">
                  <li>Nome completo;</li>
                  <li>E-mail;</li>
                  <li>Telefone;</li>
                  <li>Condomínio ou comunidade;</li>
                  <li>Endereço informado;</li>
                  <li>Número da unidade residencial;</li>
                  <li>Informações relacionadas ao cadastro.</li>
                </ul>
                <p className="text-gray-600 leading-relaxed">
                  O compartilhamento ocorrerá exclusivamente para fins de validação e segurança da comunidade.
                </p>
              </div>

              {/* 7. STATUS DE VALIDAÇÃO */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">7. STATUS DE VALIDAÇÃO</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Os usuários poderão apresentar os seguintes status:
                </p>
                <ul className="list-disc list-inside space-y-1 mb-2 text-gray-600 ml-4">
                  <li>Pendente</li>
                  <li>Aprovado</li>
                  <li>Rejeitado</li>
                  <li>Bloqueado</li>
                </ul>
                <p className="text-gray-600 leading-relaxed">
                  O Brechó Vizinho poderá criar novos status conforme a evolução da plataforma.
                </p>
              </div>

              {/* 8. APROVAÇÃO OU REJEIÇÃO */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">8. APROVAÇÃO OU REJEIÇÃO</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  A aprovação do cadastro dependerá da análise das informações disponíveis no momento da validação.
                </p>
                <p className="text-gray-600 leading-relaxed mb-2">
                  O Brechó Vizinho não possui obrigação de aprovar qualquer cadastro.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  A plataforma poderá solicitar informações complementares antes da decisão final.
                </p>
              </div>

              {/* 9. REVISÃO DE CADASTROS */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">9. REVISÃO DE CADASTROS</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Cadastros aprovados poderão ser revisados a qualquer momento quando houver:
                </p>
                <ul className="list-disc list-inside space-y-1 mb-2 text-gray-600 ml-4">
                  <li>Suspeita de fraude;</li>
                  <li>Informações inconsistentes;</li>
                  <li>Denúncias fundamentadas;</li>
                  <li>Mudança de endereço;</li>
                  <li>Necessidade operacional ou de segurança.</li>
                </ul>
                <p className="text-gray-600 leading-relaxed">
                  A plataforma poderá solicitar nova validação sempre que julgar necessário.
                </p>
              </div>

              {/* 10. RESPONSABILIDADE DAS INFORMAÇÕES */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">10. RESPONSABILIDADE DAS INFORMAÇÕES</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  O usuário declara que todas as informações fornecidas durante o cadastro são verdadeiras, completas e
                  atualizadas.
                </p>
                <p className="text-gray-600 leading-relaxed mb-2">
                  O fornecimento de informações falsas poderá resultar em:
                </p>
                <ul className="list-disc list-inside space-y-1 mb-2 text-gray-600 ml-4">
                  <li>Suspensão da conta;</li>
                  <li>Bloqueio da conta;</li>
                  <li>Exclusão da conta;</li>
                  <li>Comunicação às autoridades competentes, quando aplicável.</li>
                </ul>
              </div>

              {/* 11. LIMITAÇÕES DA VALIDAÇÃO */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">11. LIMITAÇÕES DA VALIDAÇÃO</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  A validação realizada pelo Brechó Vizinho não garante:
                </p>
                <ul className="list-disc list-inside space-y-1 mb-2 text-gray-600 ml-4">
                  <li>Idoneidade do usuário;</li>
                  <li>Solvência financeira;</li>
                  <li>Boa-fé em negociações;</li>
                  <li>Ausência de antecedentes;</li>
                  <li>Qualidade de produtos ou serviços oferecidos.</li>
                </ul>
                <p className="text-gray-600 leading-relaxed">
                  A validação apenas indica que, no momento da análise, foram observados elementos suficientes para
                  aprovação do cadastro dentro dos critérios da plataforma.
                </p>
              </div>

              {/* 12. PROTEÇÃO DOS DADOS PESSOAIS */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">12. PROTEÇÃO DOS DADOS PESSOAIS</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  O tratamento dos dados utilizados durante o processo de validação observará as disposições da Lei Geral de
                  Proteção de Dados Pessoais (LGPD) e da Política de Privacidade do Brechó Vizinho.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  Somente os dados estritamente necessários serão compartilhados para a finalidade de validação.
                </p>
              </div>

              {/* 13. SEGURANÇA DA COMUNIDADE */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">13. SEGURANÇA DA COMUNIDADE</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  A validação constitui uma das medidas adotadas pelo Brechó Vizinho para aumentar a segurança da
                  comunidade.
                </p>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Entretanto, nenhum procedimento de validação elimina integralmente riscos associados às interações entre
                  usuários.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  Os usuários permanecem responsáveis por adotar cuidados adequados em suas negociações e comunicações.
                </p>
              </div>

              {/* 14. ALTERAÇÕES DESTA POLÍTICA */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">14. ALTERAÇÕES DESTA POLÍTICA</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Esta Política poderá ser atualizada periodicamente para refletir melhorias operacionais, tecnológicas ou
                  exigências legais.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  A versão mais recente estará sempre disponível na plataforma.
                </p>
              </div>

              {/* 15. DISPOSIÇÕES FINAIS */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">15. DISPOSIÇÕES FINAIS</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  A utilização da plataforma implica concordância com esta Política de Validação.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  Ao solicitar cadastro, o usuário declara estar ciente de que seu registro poderá ser submetido aos
                  procedimentos de validação descritos neste documento.
                </p>
              </div>

              {/* ASSINATURA FINAL */}
              <div className="mt-8 pt-6 border-t border-gray-200">
                <p className="text-sm text-gray-500">
                  Última atualização: 15/06/2026<br />
                  Versão: 1.0<br />
                  Brechó Vizinho<br />
                  E-mail: <span className="text-[#449879] font-medium">info@brechovizinho.com.br</span>
                </p>
              </div>
            </div>
          </div>

          {/* Botão voltar ao topo */}
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="no-print fixed bottom-20 right-4 bg-[#449879] text-white p-3 rounded-full shadow-lg hover:bg-[#2d5f4d] transition-colors z-40"
            aria-label="Voltar ao topo"
          >
            ↑
          </button>
        </div>
      </div>
    </>
  );
}